import { useEffect, useRef, useState, type ReactNode } from "react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  X,
  Pause,
  Play,
  Ban,
} from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useUploadManager, type UploadJob } from "@/hooks/useUploadManager";
import { isUploadHost } from "@/lib/hostTenant";
import { displayPhase, SHOW_SCAN_PROGRESS } from "@/lib/scan/scanUi";
import { UploadTimelineMini } from "@/components/workspace/uploader/UploadTimeline";
import { fmtTimeLeft } from "@/lib/uploader/simpleStatus";

// Photo batches report percent by FILES DONE, not bytes: when photos are resized
// just-in-time, the byte total is seeded from the (large) originals and only
// corrected per file as each resizes, so a byte figure lags then snaps to 100%.
// Videos (few large files) keep the smoother byte-based figure.
const jobPercent = (j: UploadJob) =>
  j.kind === "image"
    ? j.total > 0
      ? Math.min(100, Math.round(((j.completed + j.failed) / j.total) * 100))
      : 0
    : j.bytesTotal > 0
      ? Math.min(100, Math.round((j.bytesSent / j.bytesTotal) * 100))
      : 0;

/** Time left in plain words ("about 2 min left") from bytes left ÷ current rate; "" if unknown. */
const fmtEta = (bytesLeft: number, bps: number): string =>
  !bps || bps <= 0 || bytesLeft <= 0 ? "" : fmtTimeLeft(bytesLeft / bps);

/**
 * Sample each active job's `bytesSent` once a second and derive a smoothed
 * transfer rate (EMA) per job. Kept in the UI layer — the upload manager already
 * accumulates real byte progress from the XHR upload events. Returns a map of
 * jobId → bytes/sec for jobs currently uploading.
 */
const useJobSpeeds = (jobs: UploadJob[]): Record<string, number> => {
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const samples = useRef<Map<string, { bytes: number; t: number; ema: number }>>(new Map());
  const [speeds, setSpeeds] = useState<Record<string, number>>({});

  useEffect(() => {
    const tick = () => {
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      const map = samples.current;
      const next: Record<string, number> = {};
      const live = new Set<string>();
      for (const j of jobsRef.current) {
        if (j.phase !== "uploading") continue;
        live.add(j.id);
        const prev = map.get(j.id);
        if (!prev) {
          map.set(j.id, { bytes: j.bytesSent, t: now, ema: 0 });
          continue;
        }
        const dt = (now - prev.t) / 1000;
        if (dt <= 0) {
          if (prev.ema > 0) next[j.id] = prev.ema;
          continue;
        }
        const inst = Math.max(0, (j.bytesSent - prev.bytes) / dt);
        // Exponential moving average keeps the number from jittering per tick.
        const ema = prev.ema === 0 ? inst : prev.ema * 0.6 + inst * 0.4;
        map.set(j.id, { bytes: j.bytesSent, t: now, ema });
        if (ema > 0) next[j.id] = ema;
      }
      // Drop samples for jobs that finished/left so a resumed id starts clean.
      for (const key of [...map.keys()]) if (!live.has(key)) map.delete(key);
      setSpeeds(next);
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return speeds;
};

/** Human "N left" from a seconds estimate. */
const fmtSecsLeft = (secs: number): string => {
  if (!secs || secs <= 0) return "";
  if (secs < 60) return `${secs}s left`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")} left`;
};
/** Wall-clock completion time from a seconds estimate. */
const fmtDoneAt = (secs: number): string => {
  if (!secs || secs <= 0) return "";
  const at = new Date(Date.now() + secs * 1000);
  return `done ~${at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
};

/**
 * Files/sec per scanning job (EMA), mirroring useJobSpeeds but on `scanCompleted`
 * — on-device face/person scanning is CPU-bound and continues after the content
 * upload finishes, so this drives the "still scanning" ETA.
 */
const useScanSpeeds = (jobs: UploadJob[]): Record<string, number> => {
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const samples = useRef<Map<string, { done: number; t: number; ema: number }>>(new Map());
  const [speeds, setSpeeds] = useState<Record<string, number>>({});

  useEffect(() => {
    const tick = () => {
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      const map = samples.current;
      const next: Record<string, number> = {};
      const live = new Set<string>();
      for (const j of jobsRef.current) {
        if (j.phase !== "scanning") continue;
        live.add(j.id);
        const done = j.scanCompleted ?? 0;
        const prev = map.get(j.id);
        if (!prev) {
          map.set(j.id, { done, t: now, ema: 0 });
          continue;
        }
        const dt = (now - prev.t) / 1000;
        if (dt <= 0) {
          if (prev.ema > 0) next[j.id] = prev.ema;
          continue;
        }
        const inst = Math.max(0, (done - prev.done) / dt);
        const ema = prev.ema === 0 ? inst : prev.ema * 0.6 + inst * 0.4;
        map.set(j.id, { done, t: now, ema });
        if (ema > 0) next[j.id] = ema;
      }
      for (const key of [...map.keys()]) if (!live.has(key)) map.delete(key);
      setSpeeds(next);
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return speeds;
};

const scanPercent = (j: UploadJob) =>
  j.scanTotal && j.scanTotal > 0
    ? Math.min(100, Math.round(((j.scanCompleted ?? 0) / j.scanTotal) * 100))
    : 0;

const isRunning = (j: UploadJob) => {
  const p = displayPhase(j.phase);
  return p === "presigning" || p === "uploading" || p === "scanning";
};

// Plain words, like the uploader's status: no stage jargon, no MB/s.
const jobLabel = (j: UploadJob) => {
  const p = displayPhase(j.phase);
  if (j.paused && isRunning(j)) return "Paused";
  if (p === "presigning") return "Getting ready…";
  if (p === "scanning") return "Finishing up…";
  if (p === "done") return j.failed > 0 ? `${j.failed.toLocaleString()} failed` : "Done";
  if (p === "error") return "Didn't upload";
  if (p === "cancelled") return "Stopped";
  return `${j.completed.toLocaleString()} of ${j.total.toLocaleString()}`;
};
/** Which part of a preview-first upload a row is: its previews or its originals. */
const jobName = (j: UploadJob) =>
  j.previewFirst === "original" ? `${j.galleryName} · Originals` : j.previewFirst === "copy" ? `${j.galleryName} · Previews` : j.galleryName;

/** Small square icon button used for the pause/resume/cancel controls. */
const IconButton = ({
  title,
  onClick,
  children,
  className,
}: {
  title: string;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    className={cn(
      "inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground shrink-0",
      className,
    )}
  >
    {children}
  </button>
);

/**
 * Floating, app-wide upload progress. Renders from the app root, so it stays
 * visible while the user navigates — uploads keep running in the background.
 */
const UploadIndicator = () => {
  const { jobs, activeCount, dismiss, clearFinished, pause, resume, cancelJob, cancelAll } =
    useUploadManager();
  const [collapsed, setCollapsed] = useState(false);
  const speeds = useJobSpeeds(jobs);
  const scanSpeeds = useScanSpeeds(jobs);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (jobs.length === 0) return null;
  // Where the uploader itself is already on screen with its own live per-file progress, the
  // floating pill is redundant: the gallery Upload tab, and the dedicated uploader page (the
  // /upload and /admin/upload routes and the upload.<domain> host). Hide it there; keep it everywhere else, where
  // it's the only sign an upload is still running after you navigate away.
  const onUploadTab =
    location.pathname.includes("/galleries/") && searchParams.get("tab") === "upload";
  const onDedicatedUploader =
    location.pathname === "/upload" ||
    location.pathname.startsWith("/upload/") ||
    location.pathname.replace(/\/+$/, "") === "/admin/upload" ||
    isUploadHost();
  if (onUploadTab || onDedicatedUploader) return null;

  const finishedCount = jobs.filter((j) =>
    ["done", "error", "cancelled"].includes(displayPhase(j.phase)),
  ).length;
  // Content upload can be done while on-device face/person scanning continues — but scanning is
  // invisible to users (SHOW_SCAN_PROGRESS), so a scanning batch counts as finished for display.
  const scanningCount = SHOW_SCAN_PROGRESS
    ? jobs.filter((j) => j.phase === "scanning").length
    : 0;
  // Still-running (pausable/cancellable) batches, and whether they're all paused.
  const runningJobs = jobs.filter(isRunning);
  const anyRunning = runningJobs.length > 0;
  const allPaused = anyRunning && runningJobs.every((j) => j.paused);
  const busy = (activeCount > 0 || scanningCount > 0) && !allPaused;
  const headerLabel = allPaused
    ? "Paused"
    : activeCount > 0
      ? "Uploading…"
      : scanningCount > 0
        ? "Finishing up…"
        : jobs.some((j) => j.failed > 0 || displayPhase(j.phase) === "error")
          ? "Uploads finished — some failed"
          : "Uploads finished";

  return (
    <div className="fixed bottom-20 md:bottom-4 right-4 z-50 w-[320px] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-background shadow-xl overflow-hidden">
      <div className="w-full flex items-center gap-1 px-3 py-2 border-b border-border bg-muted/40">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center gap-2 text-sm font-medium min-w-0 flex-1 text-left"
        >
          {allPaused ? (
            <Pause className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : busy ? (
            <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
          ) : (
            <UploadCloud className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
          <span className="truncate">{headerLabel}</span>
        </button>
        {/* Global controls: pause/resume every running batch, or cancel them all. */}
        {anyRunning &&
          (allPaused ? (
            <IconButton title="Resume all uploads" onClick={() => resume()}>
              <Play className="h-4 w-4" />
            </IconButton>
          ) : (
            <IconButton title="Pause all uploads" onClick={() => pause()}>
              <Pause className="h-4 w-4" />
            </IconButton>
          ))}
        {anyRunning && (
          <IconButton
            title="Cancel all uploads"
            onClick={() => cancelAll()}
            className="hover:text-destructive"
          >
            <Ban className="h-4 w-4" />
          </IconButton>
        )}
        <IconButton
          title={collapsed ? "Expand" : "Collapse"}
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </IconButton>
      </div>

      {!collapsed && (
        <>
          {/* A folder uploading quick-first: where it stands, in plain words. */}
          <UploadTimelineMini jobs={jobs} />
          <div className="max-h-64 overflow-y-auto divide-y divide-border">
            {jobs.map((j) => {
              const pct = jobPercent(j);
              const bps = speeds[j.id] || 0;
              const bytesLeft = Math.max(0, j.bytesTotal - j.bytesSent);
              const etaText = j.phase === "uploading" && !j.paused ? fmtEta(bytesLeft, bps) : "";
              // Scanning ETA from the on-device scan rate (files/sec).
              const scanPerSec = scanSpeeds[j.id] || 0;
              const scanLeft = Math.max(0, (j.scanTotal ?? 0) - (j.scanCompleted ?? 0));
              const scanSecs =
                j.phase === "scanning" && scanPerSec > 0 && scanLeft > 0
                  ? Math.round(scanLeft / scanPerSec)
                  : 0;
              const scanEtaText = fmtSecsLeft(scanSecs);
              const scanDoneText = fmtDoneAt(scanSecs);
              return (
                <div key={j.id} className="px-3 py-2 space-y-1.5">
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate flex items-center gap-1.5 min-w-0">
                      {displayPhase(j.phase) === "done" && j.failed === 0 && (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
                      )}
                      {(displayPhase(j.phase) === "error" || (displayPhase(j.phase) === "done" && j.failed > 0)) && (
                        <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      )}
                      {displayPhase(j.phase) === "cancelled" && (
                        <Ban className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      )}
                      <span className="truncate">{jobName(j)}</span>
                    </span>
                    <span className="flex items-center gap-1 shrink-0">
                      <span className="text-xs text-muted-foreground">{jobLabel(j)}</span>
                      {/* Per-job controls, so a single batch can be paused/cancelled. */}
                      {isRunning(j) && (
                        <>
                          {j.paused ? (
                            <IconButton
                              title="Resume this upload"
                              onClick={() => resume(j.id)}
                              className="h-5 w-5"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </IconButton>
                          ) : (
                            <IconButton
                              title="Pause this upload"
                              onClick={() => pause(j.id)}
                              className="h-5 w-5"
                            >
                              <Pause className="h-3.5 w-3.5" />
                            </IconButton>
                          )}
                          <IconButton
                            title="Cancel this upload"
                            onClick={() => cancelJob(j.id)}
                            className="h-5 w-5 hover:text-destructive"
                          >
                            <X className="h-3.5 w-3.5" />
                          </IconButton>
                        </>
                      )}
                    </span>
                  </div>
                  {(j.phase === "uploading" || j.phase === "presigning") && (
                    <Progress
                      value={j.phase === "presigning" ? undefined : pct}
                      indicatorClassName="bg-green-500"
                      className="h-1"
                    />
                  )}
                  {etaText && <div className="text-right text-[11px] text-muted-foreground">{etaText}</div>}
                  {SHOW_SCAN_PROGRESS && j.phase === "scanning" && (
                    <>
                      <Progress
                        value={scanPercent(j)}
                        indicatorClassName="bg-primary"
                        className="h-1"
                      />
                      <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground tabular-nums">
                        <span>Scanning faces &amp; people…</span>
                        <span className="text-right">
                          {scanEtaText}
                          {scanEtaText && scanDoneText ? " · " : ""}
                          {scanDoneText}
                        </span>
                      </div>
                    </>
                  )}
                  {(j.phase === "done" || j.phase === "error" || j.phase === "cancelled") && (
                    <div className="flex justify-end">
                      <button
                        onClick={() => dismiss(j.id)}
                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                      >
                        <X className="h-3 w-3" />
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {finishedCount > 0 && (
            <div className="px-3 py-2 border-t border-border">
              <Button variant="ghost" size="sm" className="w-full h-7 text-xs" onClick={clearFinished}>
                Clear finished
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UploadIndicator;
